import React, { useEffect, useState, useRef } from "react";
import AuditSelect from '../../../../components/common/AuditSelect';
import {
  hasUserAccess,
  getUserDetails,
  getUserRole,
} from "../../../../backend/auth";
import {
  useGetVendorHeirarchyRecords,
} from "../../../../backend/masters";
import { sortBy } from "underscore";
import { useAssociateCompanyLogo } from "../../../../contexts/AssociateCompanyContext";

const INCLUDE_USER_COMPANY_ASSOCIATIONS = {
  parent_company: {
    associate_companies: {
      company_location_mappings: {
        location: ['city']
      }
    }
  }
};

const ALL_OPTION = {
  value: "all",
  label: "All"
};

function extractCategories(companiesList: any[]) {
  const map = new Map<any, any>();
  (companiesList || []).forEach((c: any) => {
    (c.associate_companies || []).forEach((ac: any) => {
      (ac.locations || []).forEach((loc: any) => {
        (loc.categories || []).forEach((cat: any) => {
          if (!map.has(cat.id)) {
            map.set(cat.id, { ...cat, vendors: [...(cat.vendors || [])] });
          } else {
            const existing = map.get(cat.id);
            const existingIds = new Set(existing.vendors.map((v: any) => v.id));
            (cat.vendors || []).forEach((v: any) => {
              if (!existingIds.has(v.id)) { existing.vendors.push(v); existingIds.add(v.id); }
            });
          }
        });
      });
    });
  });
  return Array.from(map.values());
}

function extractVendors(categoriesList: any[]) {
  const seen = new Set();
  const result: any[] = [];
  (categoriesList || []).forEach((cat: any) => {
    (cat.vendors || []).forEach((v: any) => {
      if (!seen.has(v.id)) { seen.add(v.id); result.push(v); }
    });
  });
  return result;
}

function AdminVendorLocations({ onChange, previousFilters }: any) {
  const _role = getUserRole();
  const [t] = useState(new Date().getTime());
  const [associateCompanies, setAssociateCompanies] = useState<any>(null);
  const [locations, setLocations] = useState<any>(null);
  const [categories, setCategories] = useState<any>(null);
  const [vendors, setVendors] = useState<any>(null);

  // No "All" option for company either — matches the Compliance Dashboard: a
  // concrete company is always selected once the list resolves (see the
  // "companies first load" effect below).
  const [company, setCompany] = useState<any>(null);
  // "All" is back for associate company, matching the Compliance Dashboard
  // revert: it sits at ALL_OPTION until the user manually picks a specific
  // one, or a specific one is restored from back-navigation. When it's "All",
  // the sidenav logo shows the parent company's own branding (_company — see
  // the company-change effect below) rather than an auto-selected specific AC.
  const [associateCompany, setAssociateCompany] = useState<any>(ALL_OPTION);
  const [location, setLocation] = useState<any>(ALL_OPTION);
  const [category, setCategory] = useState<any>(ALL_OPTION);
  const [vendor, setVendor] = useState<any>(ALL_OPTION);
  const initialLoadComplete = useRef(false);
  const user = getUserDetails();
  const { setAssociateCompanyBranding } = useAssociateCompanyLogo();

  const { companies, isFetching }: any = useGetVendorHeirarchyRecords({
    filters: {
      user_id_eq: user.user_id,
      is_active_eq: true
    },
    // includes: INCLUDE_USER_COMPANY_ASSOCIATIONS
  });
  const sortedCompanies = sortBy(companies || [], 'name');

  useEffect(() => {
    setLocations(null);
    setLocation(ALL_OPTION);
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      if (_company) {
        const sortedACs = sortBy(_company.associate_companies || [], 'name');
        setAssociateCompanies(sortedACs);
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));

        // Restore a previously-selected associate company (back-navigation) on the
        // very first load only; otherwise reset to "All" — which shows the parent
        // company's own branding (_company, the same record already used above to
        // build the associate company list — no separate fetch needed, so this
        // applies immediately with no race/gap).
        const restored = (!initialLoadComplete.current && Object.keys(previousFilters).length &&
            company.value === (previousFilters.company || {}).value)
          ? sortedACs.find((ac: any) => ac.id == (previousFilters.associateCompany || {}).value)
          : null;
        if (restored) {
          setAssociateCompany({ value: restored.id, label: restored.name, logo: restored.logo, favicon: restored.favicon, whitelabeling_enabled: restored.whitelabeling_enabled, parent_company_id: restored.parent_company_id });
          setAssociateCompanyBranding(restored);
        } else {
          setAssociateCompany(ALL_OPTION);
          setAssociateCompanyBranding(_company);
        }
      }
    } else {
      setAssociateCompanies(null);
      const cats = extractCategories(companies);
      setCategories(cats);
      setVendors(extractVendors(cats));
      setAssociateCompany(ALL_OPTION);
      // Don't clear the logo here — company is only null at this point because the
      // company list hasn't loaded/auto-selected yet (see the companies-first-load
      // effect below), not because there's genuinely nothing to show. Clearing it
      // here flashes the default ezyCOMP logo before the real one resolves.
    }
  }, [company]);

  useEffect(() => {
    setLocations(null);
    setLocation(ALL_OPTION);
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      if (_ac) {
        setLocations(_ac.locations);
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      if (_company) {
        const cats = extractCategories([_company]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [associateCompany]);

  useEffect(() => {
    setCategory(ALL_OPTION);
    setVendor(ALL_OPTION);

    if (location && location.value !== "all" && associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      const _loc = _ac && _ac.locations.find((c: any) => c.id == location.value);
      if (_loc) {
        const cats = extractCategories([{ associate_companies: [{ locations: [_loc] }] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    } else if (associateCompany && associateCompany.value !== "all" && company && company.value !== "all") {
      const _company = companies.find((c: any) => c.id == company.value);
      const _ac = _company && _company.associate_companies.find((c: any) => c.id == associateCompany.value);
      if (_ac) {
        const cats = extractCategories([{ associate_companies: [_ac] }]);
        setCategories(cats);
        setVendors(extractVendors(cats));
      }
    }
  }, [location]);

  useEffect(() => {
    setVendor(ALL_OPTION);

    if (category && category.value !== "all") {
      const _cat = (categories || []).find((c: any) => c.id == category.value);
      if (_cat) {
        setVendors(_cat.vendors || []);
      }
    } else {
      setVendors(extractVendors(categories || []));
    }
  }, [category]);

  useEffect(() => {
    if (isFetching) return; // still loading — leave the logo hidden, not defaulted
    if (!(companies && companies.length > 0)) {
      // Loaded, but this user has no companies at all — nothing will ever populate
      // the logo otherwise, so resolve to the default now.
      setAssociateCompanyBranding(null);
      return;
    }
    const cats = extractCategories(companies);
    setCategories(cats);
    setVendors(extractVendors(cats));

    // No "All" option for company either — restore a previously-selected one
    // (back-navigation) or default to the first one alphabetically, same as the
    // Compliance Dashboard.
    const restored = Object.keys(previousFilters).length
      ? sortedCompanies.find((as: any) => as.id == (previousFilters.company || {}).value)
      : null;
    const next = restored || sortedCompanies[0] || null;
    if (!company && next) {
      setCompany({ value: next.id, label: next.name });
    }
  }, [companies, isFetching]) // eslint-disable-line react-hooks/exhaustive-deps

  // (Associate company restore-from-back-navigation now happens directly in the
  // company-change effect above, since it also needs to fall back to "All"
  // when there's nothing to restore.)

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (locations && locations.length > 0 && Object.keys(previousFilters).length) {
      if (company && company.value === (previousFilters.company || {}).value &&
        associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value) {
        const _location = locations.find((as: any) => as.id == ((previousFilters.locations || [])[0] || {}).value);
        if (_location) {
          setLocation({ value: _location.id, label: _location.name });
        }
      }
    }
  }, [locations])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (categories && categories.length > 0 && Object.keys(previousFilters).length) {
      const companyMatches = !previousFilters.company || (company && company.value === (previousFilters.company || {}).value);
      const assocMatches = !previousFilters.associateCompany || (associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value);
      if (companyMatches && assocMatches) {
        const _category = categories.find((as: any) => as.id == ((previousFilters.vendorCategories || [])[0] || {}).value);
        if (_category) {
          setCategory({ value: _category.id, label: _category.name });
        }
      }
    }
  },[categories])

  useEffect(() => {
    if (initialLoadComplete.current) return;
    if (vendors && vendors.length > 0 && Object.keys(previousFilters).length) {
      const companyMatches = !previousFilters.company || (company && company.value === (previousFilters.company || {}).value);
      const assocMatches = !previousFilters.associateCompany || (associateCompany && associateCompany.value === (previousFilters.associateCompany || {}).value);
      const categoryMatches = !((previousFilters.vendorCategories || [])[0]) || (category && category.value === ((previousFilters.vendorCategories || [])[0] || {}).value);
      if (companyMatches && assocMatches && categoryMatches) {
        const _vendor = vendors.find((as: any) => as.id == ((previousFilters.vendorRegistrations || [])[0] || {}).value);
        if (_vendor) {
          setVendor({ value: _vendor.id, label: _vendor.name });
        }
        initialLoadComplete.current = true;
      }
    }
    if (!Object.keys(previousFilters).length && vendors && vendors.length > 0) {
      initialLoadComplete.current = true;
    }
  },[vendors])

  useEffect(() => {
    // Don't call onChange until companies are loaded
    if (!companies || companies.length === 0) {
      return;
    }

    const filters: any = {};
    const filterData: any = {};

    if (company && company.value === "all") {
      const allCompanyIds = (companies || []).map((c: any) => c.id);
      if (allCompanyIds.length > 0) {
        filters.company_id_in = allCompanyIds;
      }
    } else if (company && company.value !== "all") {
      filters.company_id_eq = company.value;
      filterData.company = company;
    }

    if (associateCompany && associateCompany.value !== "all") {
      filters.associate_company_id_eq = associateCompany.value;
      filterData.associateCompany = associateCompany;
    }

    if (location && location.value !== "all") {
      filters.location_id_eq = location.value;
      filterData.locations = [location];
    }

    if (category && category.value !== "all") {
      filters.vendor_category_id_eq = category.value;
      filterData.vendorCategories = [category];
    }

    if (vendor && vendor.value !== "all") {
      filters.vendor_registration_id_eq = vendor.value;
      filterData.vendorRegistrations = [vendor];
    }

    onChange(filters, filterData);
  }, [company, associateCompany, location, category, vendor, companies]);


  function selectVendor(selected: any) {
    if(selected.value === vendor.value) {
      return
    } else {
      setVendor(selected);
    }
  }

  return (
    <>
      <div className="px-2">
        <label className="filter-label">
          <small>Company</small>
        </label>
        <AuditSelect
          placeholder="Company"
          options={sortedCompanies.map((x: any) => ({ value: x.id, label: x.name }))}
          onChange={setCompany}
          value={company}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Associate Company</small>
        </label>
        <AuditSelect
          placeholder="Associate Company"
          options={
            Array.isArray(associateCompanies)
              ? [ALL_OPTION, ...associateCompanies.map((x: any) => ({
                  value: x.id,
                  label: x.name,
                  logo: x.logo,
                  favicon: x.favicon,
                  whitelabeling_enabled: x.whitelabeling_enabled,
                  parent_company_id: x.parent_company_id,
                }))]
              : [ALL_OPTION]
          }
          onChange={(opt: any) => {
            const isAll = !opt || opt.value === "all";
            setAssociateCompany(opt || ALL_OPTION);
            if (isAll) {
              // "All" shows the parent company's own branding — the raw company
              // record (with its own logo/favicon/whitelabeling_enabled) is already
              // loaded in `companies`, so this applies immediately with no
              // dependency on a separate fetch that might not have resolved yet.
              const _company = (companies || []).find((c: any) => c.id == (company || {}).value);
              if (_company) {
                setAssociateCompanyBranding(_company);
              }
            } else {
              setAssociateCompanyBranding(opt);
            }
          }}
          value={associateCompany}
          isDisabled={!company || company.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Location</small>
        </label>
        <AuditSelect
          placeholder="Location"
          options={
            Array.isArray(locations)
              ? [
                  ALL_OPTION,
                  ...locations.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={setLocation}
          value={location}
          isDisabled={!associateCompany || associateCompany.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Vendor Category</small>
        </label>
        <AuditSelect
          placeholder="Category"
          options={
            Array.isArray(categories)
              ? [
                  ALL_OPTION,
                  ...categories.map((x: any) => {
                    return { value: x.id, label: x.name };
                  })
                ]
              : [ALL_OPTION]
          }
          onChange={setCategory}
          value={category}
          // isDisabled={!location || location.value === "all"}
        />
      </div>
      <div className="px-2">
        <label className="filter-label">
          <small>Vendor</small>
        </label>
        <AuditSelect
          placeholder="Vendors"
          options={
            Array.isArray(vendors)
              ? [
                  ALL_OPTION,
                  ...vendors.map((x: any) => ({
                    value: x.id,
                    label: x.name,
                  }))
                ]
              : [ALL_OPTION]
          }
          onChange={selectVendor}
          value={vendor}
          // isDisabled={!category || category.value === "all"}
        />
      </div>
    </>
  );
}

export default AdminVendorLocations;
